import type { EmbedOptions, ProviderConfig, ResolvedVideo } from '../types';

// `allow-popups-to-escape-sandbox` is intentional: the embed's "Open in
// Loom" / external links spawn full-page tabs that must not inherit the
// iframe's sandbox restrictions. Safe because we only ever embed URLs
// resolved through the strict hostname allowlist below.
const COMMON_SANDBOX =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation';

const LOOM_ID = /^[a-f0-9]{32}$/i;

const LOOM: ProviderConfig = {
  id: 'loom',
  label: 'Loom',
  hostnames: new Set(['loom.com', 'www.loom.com']),
  toEmbedUrl: (url, { autoplay }) => {
    const [kind, id] = url.pathname.split('/').filter(Boolean);
    if (!id || !LOOM_ID.test(id)) return null;
    if (kind !== 'share' && kind !== 'embed') return null;
    const params = new URLSearchParams();
    if (autoplay) params.set('autoplay', '1');
    const qs = params.toString();
    return `https://www.loom.com/embed/${id}${qs ? `?${qs}` : ''}`;
  },
  iframeAllow: 'autoplay; fullscreen; picture-in-picture',
  iframeSandbox: COMMON_SANDBOX,
};

export const VIDEO_PROVIDERS: ReadonlyArray<ProviderConfig> = [LOOM];

export function resolveVideoEmbed(
  input: string | null | undefined,
  options: EmbedOptions = { autoplay: true }
): ResolvedVideo | null {
  if (!input) return null;
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  const host = url.hostname.toLowerCase();
  const provider = VIDEO_PROVIDERS.find((p) => p.hostnames.has(host));
  if (!provider) return null;
  const embedUrl = provider.toEmbedUrl(url, options);
  if (!embedUrl) return null;
  return { provider, embedUrl, rawUrl: input };
}
